import type { AuditActorSummary, AuditEventSummary, AuditResourceSummary, MembershipSummary } from '@app/types/org-console';

type AuditActorLike = AuditActorSummary & {
  DisplayName?: string;
  display_name?: string;
  Name?: string;
  Email?: string;
  email_address?: string;
  ID?: string;
  userId?: string;
  user_id?: string;
  Type?: string;
  actorType?: string;
  actor_type?: string;
};

type AuditResourceLike = AuditResourceSummary & {
  ID?: string;
  Name?: string;
  display_name?: string;
  resourceName?: string;
  resource_name?: string;
  resourceId?: string;
  resource_id?: string;
  Type?: string;
};

type AuditEventLike = AuditEventSummary & {
  Actor?: AuditActorLike;
  Resource?: AuditResourceLike;
  Metadata?: Record<string, unknown>;
};

export interface AuditNameLookups {
  actorNamesById: Record<string, string>;
  resourceNamesById: Record<string, string>;
}

const emptyAuditNameLookups: AuditNameLookups = {
  actorNamesById: {},
  resourceNamesById: {}
};

const systemActorID = '00000000-0000-0000-0000-000000000000';

const normalizeString = (value?: string | null): string | undefined => {
  if (value == null) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeActorEmailForDisplay = (value?: string | null): string | undefined => {
  const email = normalizeString(value);
  if (!email) {
    return undefined;
  }
  const match = email.match(/^([^@+]+)\+([a-f0-9]{8,})@([^@]+)$/i);
  if (!match) {
    return email;
  }
  return `${match[1]}@${match[3]}`;
};

const normalizeAuditActor = (actor?: AuditActorLike | null): AuditActorSummary | undefined => {
  if (!actor) {
    return undefined;
  }

  return {
    type: normalizeString(actor.type ?? actor.Type ?? actor.actorType ?? actor.actor_type),
    id: normalizeString(actor.id ?? actor.ID ?? actor.userId ?? actor.user_id),
    displayName: normalizeString(actor.displayName ?? actor.DisplayName ?? actor.display_name ?? actor.Name),
    email: normalizeString(actor.email ?? actor.Email ?? actor.email_address)
  };
};

const normalizeAuditResource = (resource?: AuditResourceLike | null): AuditResourceSummary | undefined => {
  if (!resource) {
    return undefined;
  }

  return {
    type: normalizeString(resource.type ?? resource.Type),
    id: normalizeString(resource.id ?? resource.ID ?? resource.resourceId ?? resource.resource_id),
    name: normalizeString(resource.name ?? resource.Name ?? resource.resourceName ?? resource.resource_name ?? resource.display_name)
  };
};

export const normalizeAuditEvent = (event: AuditEventSummary): AuditEventSummary => {
  const eventLike = event as AuditEventLike;
  const metadata = eventLike.metadata ?? eventLike.Metadata;

  return {
    ...event,
    metadata: (metadata ?? undefined) as Record<string, unknown> | undefined,
    actor: normalizeAuditActor((eventLike.actor ?? eventLike.Actor) as AuditActorLike | undefined),
    resource: normalizeAuditResource((eventLike.resource ?? eventLike.Resource) as AuditResourceLike | undefined)
  };
};

const metadataString = (event: AuditEventSummary, key: string): string | undefined => {
  const value = event.metadata?.[key];
  return typeof value === 'string' ? normalizeString(value) : undefined;
};

const metadataStringArray = (metadata: Record<string, unknown> | undefined, key: string): string[] => {
  const value = metadata?.[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => (typeof entry === 'string' ? normalizeString(entry) : undefined))
    .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
};

const formatDelimitedLabel = (value: string): string => {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(', ');
};

export const getAuditEventLabel = (event: AuditEventSummary): string => {
  const operation = metadataString(event, 'operation');
  const triggerType = metadataString(event, 'trigger_type') ?? metadataString(event, 'triggerType');
  if (event.type === 'discovery.discovery_run.start' && triggerType) {
    return `discovery.discovery_run.start.${triggerType}`;
  }
  if (event.type === 'connectors.connector.update.noop' && operation === 'userdata_download') {
    return 'connectors.connector.userdata.download';
  }
  if (event.type === 'connectors.connector.update.success' && operation === 'enrollment_reset') {
    return 'connectors.connector.enrollment.reset';
  }
  return event.type ?? '—';
};

const titleCase = (value: string): string => {
  if (!value) {
    return value;
  }
  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ''))
    .join(' ')
    .trim();
};

const identityName = (membership?: MembershipSummary): string | undefined => {
  return normalizeString(membership?.user?.displayName ?? membership?.user?.name ?? membership?.user?.email);
};

export const buildAuditNameLookups = (memberships?: MembershipSummary[] | null): AuditNameLookups => {
  if (!memberships || memberships.length === 0) {
    return emptyAuditNameLookups;
  }

  const actorNamesById: Record<string, string> = {};
  const resourceNamesById: Record<string, string> = {};

  memberships.forEach((membership) => {
    const resolvedName = identityName(membership);
    if (!resolvedName) {
      return;
    }

    const membershipId = normalizeString(membership.id);
    if (membershipId) {
      resourceNamesById[membershipId] = resolvedName;
    }

    const userId = normalizeString(membership.userId ?? membership.user?.id);
    if (userId) {
      actorNamesById[userId] = resolvedName;
    }
  });

  return {
    actorNamesById,
    resourceNamesById
  };
};

const ACTOR_METADATA_NAME_KEYS = new Set([
  'actordisplayname',
  'actorname',
  'username',
  'userdisplayname',
  'performedby',
  'performedbyname',
  'principalname',
  'subjectname',
  'initiatedby',
  'initiatedbyname'
]);

const RESOURCE_METADATA_NAME_KEYS = new Set([
  'resourcename',
  'targetname',
  'membershipname',
  'sitename',
  'clustername',
  'connectorname',
  'credentialname',
  'devicename'
]);

const normalizeMetadataKey = (key: string): string => key.replace(/[^a-z0-9]/gi, '').toLowerCase();

const findMetadataString = (metadata: Record<string, unknown> | undefined, keys: Set<string>): string | undefined => {
  if (!metadata) {
    return undefined;
  }

  const stack: unknown[] = [metadata];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current || typeof current !== 'object') {
      continue;
    }

    Object.entries(current as Record<string, unknown>).forEach(([rawKey, value]) => {
      if (typeof value === 'string' && keys.has(normalizeMetadataKey(rawKey))) {
        const normalized = normalizeString(value);
        if (normalized) {
          stack.length = 0;
          stack.push({ __match: normalized });
          return;
        }
      }

      if (value && typeof value === 'object') {
        stack.push(value);
      }
    });

    const match = current as { __match?: string };
    if (typeof match.__match === 'string') {
      return match.__match;
    }
  }

  return undefined;
};

export const getAuditActorLabel = (
  actor?: AuditActorSummary | null,
  options?: { lookups?: AuditNameLookups; metadata?: Record<string, unknown> | null }
): string => {
  if (!actor) {
    return '—';
  }

  if (actor.displayName) {
    return actor.displayName;
  }

  const lookupName = actor.id ? options?.lookups?.actorNamesById[actor.id] : undefined;
  if (lookupName) {
    return lookupName;
  }

  const normalizedEmail = normalizeActorEmailForDisplay(actor.email);
  if (normalizedEmail) {
    return normalizedEmail;
  }

  if (actor.email) {
    return actor.email;
  }

  const metadataName = findMetadataString(options?.metadata ?? undefined, ACTOR_METADATA_NAME_KEYS);
  if (metadataName) {
    return metadataName;
  }

  if (actor.id === systemActorID) {
    return 'System';
  }

  if (actor.id) {
    return actor.id;
  }

  if (actor.type) {
    return titleCase(actor.type);
  }

  return '—';
};

export const getAuditResourceLabel = (
  event: Pick<AuditEventSummary, 'resource' | 'metadata'>,
  lookups?: AuditNameLookups
): string => {
  const resource = event.resource;
  if (!resource) {
    return '—';
  }

  if (resource.type === 'idp_mapping_rule') {
    if (resource.name) {
      return formatDelimitedLabel(resource.name);
    }
    const roleSlugs = metadataStringArray(event.metadata, 'role_slugs');
    if (roleSlugs.length > 0) {
      return roleSlugs.join(', ');
    }
    const matchValue = typeof event.metadata?.match_value === 'string' ? normalizeString(event.metadata.match_value) : undefined;
    if (matchValue) {
      return matchValue;
    }
  }

  if (resource.name) {
    return resource.name;
  }

  const lookupName = resource.id ? lookups?.resourceNamesById[resource.id] : undefined;
  if (lookupName) {
    return lookupName;
  }

  const metadataName = findMetadataString(event.metadata, RESOURCE_METADATA_NAME_KEYS);
  if (metadataName) {
    return metadataName;
  }

  if (resource.id) {
    return resource.id;
  }

  return '—';
};
